/**
 * WHAT THE BACK OFFICE HAS, AND WHAT IT ONLY SKETCHES.
 *
 * Both are in the navigation on purpose. A customer seeing "Nákup ·
 * připravujeme" learns the intended shape of the product, and their reaction to
 * the greyed-out items is the most useful thing in a demo — it tells you what
 * to build next, which no amount of asking does.
 *
 * The honesty is structural rather than promised. A `preview` screen renders
 * from a static descriptor and lives under `src/preview/`, and
 * `test/preview-isolation.spec.ts` fails if any file there imports the API
 * client. A mock-up that physically cannot reach real data cannot accidentally
 * show a real number, and cannot rot into a half-wired screen that nobody
 * remembers is fake.
 */

import type { IconName } from "@gf/ui";
import type { MessageKey } from "./locales";

export type ScreenId =
  | "today"
  | "sales"
  | "checks"
  | "shifts"
  | "kitchen"
  | "fiscal"
  | "menu"
  | "tableCards"
  | "staff"
  | "settings"
  | "stock"
  | "reservations"
  | "history"
  | "printers"
  | "preview:purchasing"
  | "preview:rota"
  | "preview:analytics"
  | "preview:loyalty";

export interface NavItem {
  readonly id: ScreenId;
  readonly label: MessageKey;
  /** Drawn before the label and adding no text to it — see `ConsoleApp.vue`. */
  readonly icon: IconName;
  /** Drawn only. Nothing behind it can reach the network. */
  readonly preview?: true;
  /** The module the screen belongs to (`@gf/contracts/module/catalogue`); absent is the core. */
  readonly module?: string;
}

export interface NavGroup {
  readonly label: MessageKey;
  readonly items: readonly NavItem[];
}

export const NAV: readonly NavGroup[] = [
  {
    label: "nav.group.running",
    items: [
      { id: "today", label: "nav.today", icon: "house" },
      { id: "checks", label: "nav.checks", icon: "receipt" },
      { id: "reservations", label: "nav.reservations", icon: "calendar", module: "reservations" },
      { id: "kitchen", label: "nav.kitchen", icon: "chefHat", module: "kitchen-display" },
      { id: "printers", label: "nav.printers", icon: "printer" },
      { id: "shifts", label: "nav.shifts", icon: "clock" },
      { id: "stock", label: "nav.stock", icon: "package", module: "stock" },
    ],
  },
  {
    label: "nav.group.money",
    items: [
      { id: "sales", label: "nav.sales", icon: "trendingUp" },
      { id: "fiscal", label: "nav.fiscal", icon: "landmark" },
    ],
  },
  {
    label: "nav.group.setup",
    items: [
      { id: "menu", label: "nav.menu", icon: "bookOpen" },
      { id: "tableCards", label: "nav.tableCards", icon: "qrCode", module: "guest-ordering" },
      { id: "staff", label: "nav.staff", icon: "users" },
      { id: "history", label: "nav.history", icon: "history" },
      { id: "settings", label: "nav.settings", icon: "settings" },
    ],
  },
  {
    label: "nav.group.soon",
    items: [
      { id: "preview:purchasing", label: "preview.purchasing", icon: "fileText", preview: true },
      { id: "preview:rota", label: "preview.rota", icon: "calendarDays", preview: true },
      { id: "preview:analytics", label: "preview.analytics", icon: "chartColumn", preview: true },
      { id: "preview:loyalty", label: "preview.loyalty", icon: "heart", preview: true },
    ],
  },
];

/**
 * What a venue's office offers. The "Připravujeme" drawings are for the demo —
 * showing where the product goes — and never in a venue's own office, where a
 * screen that does nothing is a promise somebody has to keep.
 */
export const navFor = (previews: boolean, effective?: readonly string[]): readonly NavGroup[] =>
  (previews ? NAV : NAV.filter((group) => !group.items.some((item) => item.preview)))
    .map((group) => ({
      ...group,
      // A screen for a module the venue does not have is not in its office. Until
      // the venue's modules are known (`undefined`) everything shows.
      items: group.items.filter((item) => !item.module || !effective || effective.includes(item.module)),
    }))
    .filter((group) => group.items.length > 0);

/** Whether a screen is reachable at a venue with these modules. */
export const screenAvailable = (screen: ScreenId, effective?: readonly string[]): boolean => {
  const item = NAV.flatMap((g) => g.items).find((i) => i.id === screen);
  return !item?.module || !effective || effective.includes(item.module);
};

const IDS = new Set<string>(NAV.flatMap((g) => g.items.map((i) => i.id)));

export const isScreenId = (value: string): value is ScreenId => IDS.has(value);

/**
 * The screen a URL fragment names, or the first one.
 *
 * A fragment rather than a path because the console is served as a static
 * bundle and a deep link must survive a reload without a server rewrite rule —
 * and because the one thing worse than no deep link is a deep link that 404s on
 * somebody else's machine.
 */
export function screenFromHash(hash: string): ScreenId {
  const raw = hash.replace(/^#\/?/, "").split("?")[0] ?? "";
  return isScreenId(raw) ? raw : "today";
}

/** The order id a check-detail fragment carries, if it is one. */
export function orderFromHash(hash: string): string | null {
  const match = /[?&]order=([0-9a-fA-F-]{36})/.exec(hash);
  return match?.[1] ?? null;
}
